import { SITE_ANALYSING, isLiveSection, type SiteReadTreatment, type SiteKind, type SiteSectionRead } from "./siteSearch";

/** What the Sources page says about a website (the founder, 2026-10-22: "The display says what the AI knows, in plain words"; the
 *  send-and-sections round, 2026-10-23: "the word section appears nowhere a person reads"): the Kind cell ("Website" or "Forum"), the row's
 *  read line in pages ("812 pages read · 40 left out · the forum is searched when needed"), the site dialog's cap line in words and its list
 *  of the forum parts searched when needed. Pure; the API answers the parts with their counts and the source's `leftOut`. */
export type WebsiteWordsSource = { location?: string; siteKind?: SiteKind | null; treatment?: SiteReadTreatment | null; unreadable?: boolean; sections?: readonly SiteSectionRead[]; items: number; /** the pages the "Leave out" list leaves out */ leftOut?: number };
type Opt = { liveSearch: boolean };

const n = (x: number) => x.toLocaleString("en-US");
const plural = (x: number, one: string, many = `${one}s`) => `${n(x)} ${x === 1 ? one : many}`;
const analysing = (s: WebsiteWordsSource) => !s.treatment || !s.siteKind || s.treatment === SITE_ANALYSING;
/** a whole forum searched live (nothing of it is read) */
const wholeForumLive = (s: WebsiteWordsSource) => s.siteKind === "forum" && s.treatment === "searched";
const NOT_SEARCHED = "Not searched (no search on this server)";
/** the forum parts searched when needed (a part that is not a forum is never searched, whatever was stored) */
const liveForums = (s: WebsiteWordsSource) => (s.sections ?? []).filter((x) => x.state === "live" && isLiveSection(x));

/** The Kind cell: "Website", "Forum" for a forum through and through (also one the owner stores), "Analysing" before the first decision.
 *  Never a content kind: what is read is the read line's. */
export function websiteLabel(s: WebsiteWordsSource, _o?: Opt): string {
  if (analysing(s)) return "Analysing";
  return s.siteKind === "forum" ? "Forum" : "Website";
}

/** the line's forum part: "the forum is searched when needed", "2 forums are searched when needed", or "… not searched" without live search */
function forumPart(count: number, o: Opt): string | null {
  if (!count) return null;
  const how = o.liveSearch ? "searched when needed" : "not searched";
  return count === 1 ? `the forum is ${how}` : `${n(count)} forums are ${how}`;
}

/** The row's line, in pages: "812 pages read", then what the "Leave out" list leaves out ("· 40 left out"), then the forum parts. A whole forum
 *  reads "Searched when needed", a site that answers only challenges "Not readable: searched when needed". What the plan's words stopped is
 *  the dialog's cap line, never this line (a member who may not buy is not told). Null while analysing. */
export function websiteReadLine(s: WebsiteWordsSource, o: Opt): string | null {
  if (analysing(s)) return null;
  if (s.unreadable) return o.liveSearch ? "Not readable: searched when needed" : `Not readable: ${NOT_SEARCHED.toLowerCase()}`;
  if (wholeForumLive(s)) return o.liveSearch ? "Searched when needed" : NOT_SEARCHED;
  const pages = (s.sections ?? []).filter((x) => x.state === "read").reduce((a, x) => a + x.pages, 0);
  return [`${plural(pages, "page")} read`, s.leftOut ? `${n(s.leftOut)} left out` : null, forumPart(liveForums(s).length, o)].filter(Boolean).join(" · ");
}

/** The site dialog's cap line when the plan's words stopped the read, with the project's words used and its cap: "28,000 of 50,000 words read:
 *  upgrade for the rest"; null when nothing was left out for the plan (a crawler limit is no upgrade). */
export function websiteCapSentence(s: WebsiteWordsSource, words: { used: number; cap: number }): string | null {
  if (!(s.sections ?? []).some((x) => x.state === "not_read" && x.why === "cap")) return null;
  return `${n(words.used)} of ${n(words.cap)} words read: upgrade for the rest`;
}

/** one forum part of the dialog's first list: its path and the address a person reads ("omnalingo.com/forum/") */
export type WebsiteSearchedPart = { path: string; address: string };
/** the host a person reads ("omnalingo.com"), or "" for an address that does not parse */
function hostOf(location: string | undefined): string {
  try { return new URL(location ?? "").host; } catch { return ""; }
}
/** The dialog's first list, "Searched when needed": the forum parts still searched, in the check's order; empty when there are none (the list
 *  is then absent). A whole forum is the row's own line, not a part. */
export function websiteSearchedParts(s: WebsiteWordsSource, _o?: Opt): WebsiteSearchedPart[] {
  if (analysing(s) || s.unreadable || wholeForumLive(s)) return [];
  const host = hostOf(s.location);
  return liveForums(s).map((x) => ({ path: x.path, address: `${host}${x.path}` }));
}
